import React from 'react'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  useFormContext,
  FormInputController,
  FormInputControllerProps,
  splitStyleProps,
  FormHelperText,
  forwardRefAs,
  AsProp,
} from '@thirstycamel/ui'

export type InputProps = {
  as: AsProp
  name: string
  onChangeValue?: (value: any) => void
  label?: string
  isRequired?: boolean
  appearsRequired?: boolean
  helperText?: string | React.ReactNode
  rules?: FormInputControllerProps['rules']
  hideErrors?: boolean
  error?: any
  type?: string
  isReadOnly?: boolean
  isDisabled?: boolean
}

export const Input = forwardRefAs<InputProps, 'input'>(
  (
    {
      as,
      name,
      onChangeValue,
      label,
      isRequired,
      appearsRequired,
      isDisabled,
      isReadOnly,
      helperText,
      error,
      hideErrors,
      type,
      ...restProps
    },
    ref,
  ) => {
    const { control, setValue, errors } = useFormContext()

    const handleChangeValue = value => {
      setValue(name, value)

      onChangeValue && onChangeValue(value)
    }

    const [styleProps, inputProps] = splitStyleProps(restProps)

    const _error = error?.message || errors[name]?.message

    return (
      <FormControl
        {...styleProps}
        isInvalid={_error}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
      >
        {label && (
          <FormLabel htmlFor={name} textTransform="none" isRequired={appearsRequired}>
            {label}
          </FormLabel>
        )}

        <FormInputController
          as={as}
          control={control}
          name={name}
          onChangeValue={handleChangeValue}
          isInvalid={!!_error}
          ref={ref}
          type={type}
          {...inputProps}
        />

        {!hideErrors && type !== 'hidden' && (
          <FormErrorMessage name={name} error={error?.message} />
        )}

        {helperText && <FormHelperText children={helperText} />}
      </FormControl>
    )
  },
)

export default Input
